// src/utils/qrShare.js
//
// Шаринг и сохранение QR-кодов.
//
// Сценарии работы:
//   1. Capacitor (Android, iOS) — нативный Share API через @capacitor/share,
//      сохранение в галерею через @capacitor/filesystem.
//   2. Веб — Web Share API (с файлом если поддерживается) или скачивание файла.
//
// Главные функции:
//   - shareQR({ svgEl, filename, title, text })
//   - saveQR({ svgEl, filename })

import { isWeb } from './platform';

// Рендерим <svg> в PNG-base64 строку (без префикса "data:image/png;base64,")
async function svgToPngBase64(svgEl, size = 600) {
  if (!svgEl) throw new Error('SVG element not provided');
  const svgString = new XMLSerializer().serializeToString(svgEl);
  // Защита от проблем с пробелами/кириллицей: кодируем через utf-8
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = (e) => reject(new Error('Image load failed'));
      im.src = url;
    });

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // Белый фон (QR должен быть с белым фоном для нормального скана из фото)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    // Рисуем QR с небольшим padding'ом
    const pad = Math.round(size * 0.06);
    ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);

    const dataUrl = canvas.toDataURL('image/png');
    // Убираем "data:image/png;base64,"
    return dataUrl.split(',', 2)[1];
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Загружаем Capacitor динамически (его может не быть в вебе)
function loadCapacitor() {
  try {
    // eslint-disable-next-line
    const cap = require('@capacitor/core');
    // eslint-disable-next-line
    const share = require('@capacitor/share');
    // eslint-disable-next-line
    const fs = require('@capacitor/filesystem');
    return { Capacitor: cap.Capacitor, Share: share.Share, Filesystem: fs.Filesystem, Directory: fs.Directory };
  } catch (e) {
    return null;
  }
}

// PNG-base64 → Blob (для веб-шаринга и скачивания)
function base64ToBlob(b64, mime = 'image/png') {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// ───────────────────────────────────────────────────────────────
// Шаринг QR — открывает системный диалог "поделиться".
//
// opts:
//   svgEl    — DOM-элемент <svg> с QR
//   filename — имя файла (например, 'alsatuu-qr.png')
//   title    — короткий заголовок (для некоторых получателей)
//   text     — текст сообщения, который пойдёт вместе с картинкой
// ───────────────────────────────────────────────────────────────
export async function shareQR({ svgEl, filename = 'alsatuu-qr.png', title = 'Alsatuu', text = '' }) {
  const base64 = await svgToPngBase64(svgEl);

  // 1. Capacitor (нативно)
  const cap = loadCapacitor();
  if (cap && cap.Capacitor && cap.Capacitor.isNativePlatform && cap.Capacitor.isNativePlatform()) {
    const { Share, Filesystem, Directory } = cap;
    // Пишем во временный кэш и шарим как файл
    const write = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    await Share.share({
      title,
      text,
      url: write.uri,        // url для file://… — нативный шарящий приложение это понимает
      dialogTitle: title,
    });
    return { ok: true, method: 'capacitor' };
  }

  // 2. Web Share API с файлом (если поддерживается)
  const blob = base64ToBlob(base64);
  const file = new File([blob], filename, { type: 'image/png' });
  if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return { ok: true, method: 'web-share' };
    } catch (e) {
      // пользователь отменил или ошибка — фолбэк ниже
      if (e && e.name === 'AbortError') return { ok: false, cancelled: true };
    }
  }

  // 3. Fallback: скачиваем файл + копируем текст в буфер
  downloadBlob(blob, filename);
  if (text && navigator.clipboard && navigator.clipboard.writeText) {
    try { await navigator.clipboard.writeText(text); } catch (e) {}
  }
  return { ok: true, method: 'download-fallback' };
}

// ───────────────────────────────────────────────────────────────
// Сохранение QR на устройство.
//   - Capacitor: пишем в Documents (для веб-сборок Capacitor — это Files-приложение)
//   - Веб: обычное скачивание
// ───────────────────────────────────────────────────────────────
export async function saveQR({ svgEl, filename = 'alsatuu-qr.png' }) {
  const base64 = await svgToPngBase64(svgEl);

  const cap = loadCapacitor();
  if (cap && cap.Capacitor && cap.Capacitor.isNativePlatform && cap.Capacitor.isNativePlatform()) {
    const { Filesystem, Directory } = cap;
    // Documents виден пользователю в Files-приложении
    const res = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Documents,
    });
    return { ok: true, method: 'capacitor', uri: res.uri };
  }

  const blob = base64ToBlob(base64);
  downloadBlob(blob, filename);
  return { ok: true, method: 'web-download' };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ───────────────────────────────────────────────────────────────
// Распознавание QR из загруженного файла (для сканеров покупателя).
// Принимает File, возвращает строку токена/JSON.
// Использует html5-qrcode (уже подключён).
// ───────────────────────────────────────────────────────────────
export async function scanQRFromFile(file) {
  // eslint-disable-next-line
  const { Html5Qrcode } = require('html5-qrcode');
  // Временный контейнер для библиотеки (она требует id-элемент даже в file mode)
  const containerId = `__qr_file_scanner_${Date.now()}`;
  const container = document.createElement('div');
  container.id = containerId;
  container.style.display = 'none';
  document.body.appendChild(container);

  const scanner = new Html5Qrcode(containerId);
  try {
    // showImage=false — не показываем превью, нам только текст нужен
    const decodedText = await scanner.scanFile(file, false);
    return decodedText;
  } finally {
    try { scanner.clear(); } catch (e) {}
    if (container.parentNode) container.parentNode.removeChild(container);
  }
}

// Используется для понимания, нужно ли показывать кнопку "сохранить" отдельно от "поделиться".
// На вебе они делают почти одно и то же (скачивание), поэтому в вебе достаточно одной "поделиться".
export function hasNativeShare() {
  const cap = loadCapacitor();
  return !!(cap && cap.Capacitor && cap.Capacitor.isNativePlatform && cap.Capacitor.isNativePlatform());
}

// Маркер чтобы не дёргать isWeb напрямую в компонентах
export function isWebPlatform() {
  return isWeb();
}
